import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { ScheduleEntity } from '../../apis/schedule/schedule-entity';
import { ScheduleRowEntity } from '../../apis/schedule/schedule-row-entity';
import { EventEntity } from '../../apis/event/event-entity';
import { EventCommentEntity } from '../../apis/event/event-comment-entity';
import { IEventPresenterScheduleData, IEventPresenterViewModel } from '../../components/info/event-presenter';
import { EventStatisticsApi } from '../../apis/event-statistics/event-statistics-api';
import { EventStatisticsEntity } from '../../apis/event-statistics/event-statistics-entity';
import { GroupEntity } from '../../apis/group/group-entity';
import { PresentApi } from '../../apis/present/present-api';

@inject(IRouter, PresentApi, EventStatisticsApi)
export class PresentEvent {
    public event: EventEntity = null;
    public schedules: PresentEventViewModel[] = null;
    public groups: GroupEntity[] = [];
    public statistics: EventStatisticsEntity = null;
    public comments: EventCommentEntity[] = [];
    public isLoading: boolean = false;
    public onCancelCallback: () => void;
    private _code: string = null;

    constructor(
        private readonly _router: IRouter,
        private readonly _presentApi: PresentApi,
        private readonly _statisticsApi: EventStatisticsApi,
    ) {
        this.onCancelCallback = () => void this._router.load('present');
    }

    public loading(params: any): void {
        this._code = params?.code ? String(params.code).toUpperCase() : null;
    }

    public async attached(): Promise<void> {
        if (!this._code) {
            void this._router.load('present');
            return;
        }
        this.isLoading = true;
        try {
            const presentation = await this._presentApi.getByCode(this._code);
            this.event = presentation.event;
            this.groups = presentation.groups;
            this.comments = presentation.comments;
            this.schedules = presentation.schedules.map(s => new PresentEventViewModel(s.schedule, {
                serie: s.serie,
                customIntervals: s.customIntervals,
                venues: presentation.venues,
                teams: s.teams,
                fields: s.fields,
            }));
            this.isLoading = false;
        } catch {
            void this._router.load('present');
            return;
        }
        // Statistics tracking is non-fatal
        try {
            this.statistics = await this._statisticsApi.trackView(this.event.id);
        } catch {
            // ignore
        }
    }
}

export class PresentEventViewModel implements IEventPresenterViewModel {
    private _entity: ScheduleEntity;
    private _presentationData: IEventPresenterScheduleData;

    constructor(entity: ScheduleEntity, presentationData: IEventPresenterScheduleData) {
        this._entity = entity;
        this._presentationData = presentationData;
    }

    public get schedule(): ScheduleEntity { return this._entity; }
    public get name(): string { return this._entity.name; }
    public get code(): string { return this._entity.code; }
    public get games(): ScheduleRowEntity[] { return this.schedule.games; }

    // Data already arrives nested from PresentApi.getByCode — no further fetch needed.
    public async getPresentationData(): Promise<IEventPresenterScheduleData> {
        return this._presentationData;
    }
}

